import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormFieldAndLabelFactory } from '../../../components/Form/FormFieldAndLabelFactory';
import { ISimpleChoice } from '../../../data-models/field2.data-model';
import {
  DownloadConfig,
  DownloadRequest,
  downloadRequestSchema,
  ExportFormat,
  FilterValue,
} from '../../../schemas/DownloadConfig.schema';
import { downloadTableData, triggerFileDownload } from '../../../services/queries/MaggieInvestigatorQueries';
import { schemaToFormFields } from '../../../util/schema-utils';
import { captureFormValidationError } from '../../../util/SentryUtils';
import { IFormField } from '../../../view-models/form.view-model';
import { FilterBuilder } from './FilterBuilder';

const FormFieldContainerCss = css`
  & .MuiFormControl-root {
    margin: 0 !important;
  }
`;

interface ISortOption {
  columnName: string;
  displayName: string;
}

interface ITableDownloadDialogProps {
  config: DownloadConfig;
  open: boolean;
  onClose: () => void;
  /** Seeds `filters` for this mount only — e.g. opening the dialog for one
   *  delivery's rows. The dialog fully unmounts on close (see the caller),
   *  so there's nothing to reset on reopen or config change. */
  initialFilters?: Record<string, string | number | boolean | FilterValue>;
  /** Sort By choices beyond `config.filterableColumns` — for columns the server
   *  accepts as a sort but rejects as a filter. */
  extraSortOptions?: ISortOption[];
  defaultSort?: { sortBy: string; sortOrder: 'ASC' | 'DESC' };
}

export function TableDownloadDialog({
  config,
  open,
  onClose,
  initialFilters,
  extraSortOptions,
  defaultSort,
}: ITableDownloadDialogProps) {
  const [filters, setFilters] = useState<Record<string, string | number | boolean | FilterValue>>(
    () => initialFilters ?? {}
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { schema, fieldsMap, defaultValues } = useDownloadForm(config, extraSortOptions, defaultSort);

  const methods = useForm({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(schema),
  });

  const handleDownload = async () => {
    const isValid = await methods.trigger();
    if (!isValid) {
      setError('Please fill in all required fields');
      setIsDownloading(false);
      captureFormValidationError('Table download form', methods.formState.errors);
      return;
    }

    setIsDownloading(true);
    setError(null);

    const { format, sortBy, sortOrder, limit, offset } = methods.getValues() as DownloadRequest;

    try {
      const request: DownloadRequest = {
        tableName: config.tableName,
        format,
        filters: Object.keys(filters).length > 0 ? filters : undefined,
        sortBy: sortBy || undefined,
        sortOrder: sortBy ? sortOrder : null,
        limit,
        offset,
      };

      const blob = await downloadTableData(request);
      const extension = format === ExportFormat.CSV ? 'csv' : 'xlsx';
      const filenameStem = config.displayName.replace(/\W+/g, '_').replace(/^_+|_+$/g, '');
      const filename = `${filenameStem}_${new Date().toISOString().split('T')[0]}.${extension}`;

      triggerFileDownload(blob, filename);
      onClose();
    } catch (err) {
      console.error('Download failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to export table data');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFilterChange = (columnName: string, value: string | number | boolean | FilterValue | null) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (value === null || value === undefined || value === '') {
        delete newFilters[columnName];
      } else {
        newFilters[columnName] = value;
      }
      return newFilters;
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Typography variant='h5'>Export {config.displayName}</Typography>
          <IconButton edge='end' onClick={onClose} aria-label='close'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {config.description && (
            <Typography variant='body2' color='text.secondary'>
              {config.description}
            </Typography>
          )}

          {error && <Alert severity='error'>{error}</Alert>}
          <FormProvider {...methods}>
            <DownloadForm fieldsMap={fieldsMap} />
          </FormProvider>

          {/* Filters */}
          {config.filterableColumns.length > 0 && (
            <Box>
              <Typography variant='subtitle2' gutterBottom>
                Filters (Optional)
              </Typography>
              <FilterBuilder
                columns={config.filterableColumns}
                filters={filters}
                onFilterChange={handleFilterChange}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 10, justifyContent: 'start' }}>
        <LoadingButton
          variant='contained'
          color='secondary'
          onClick={handleDownload}
          loading={isDownloading}
          startIcon={<DownloadIcon />}
          disabled={isDownloading}
        >
          {isDownloading ? 'Exporting...' : 'Export'}
        </LoadingButton>
        <Button onClick={onClose} disabled={isDownloading} variant='outlined' color='secondary'>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DownloadForm({ fieldsMap }: { fieldsMap: Map<string, IFormField<unknown>> }) {
  return (
    <div css={FormFieldContainerCss}>
      <FormFieldAndLabelFactory formField={fieldsMap.get('format')!} />

      {/* Pagination Options */}
      <Box mt={6}>
        <Typography variant='subtitle2' gutterBottom>
          Pagination Options
        </Typography>
        <Stack display='grid' gridTemplateColumns='1fr 1fr' gap={2} width='100%'>
          <FormFieldAndLabelFactory formField={fieldsMap.get('limit')!} />
          <FormFieldAndLabelFactory formField={fieldsMap.get('offset')!} />
        </Stack>
      </Box>

      {/* Sort Options */}
      {fieldsMap.get('sortBy') != null && (
        <Box mt={4}>
          <Typography variant='subtitle2' gutterBottom>
            Sort Options
          </Typography>
          <Stack display='grid' gridTemplateColumns='1fr 1fr' gap={2} width='100%'>
            <FormFieldAndLabelFactory formField={fieldsMap.get('sortBy')!} />
            <FormFieldAndLabelFactory formField={fieldsMap.get('sortOrder')!} />
          </Stack>
        </Box>
      )}
    </div>
  );
}

function useDownloadForm(
  config: DownloadConfig,
  extraSortOptions?: ISortOption[],
  defaultSort?: { sortBy: string; sortOrder: 'ASC' | 'DESC' }
) {
  const sortByOptions: ISimpleChoice<string>[] | null = useMemo(() => {
    const options = [
      ...config.filterableColumns.map((col) => ({ value: col.columnName, displayName: col.displayName })),
      ...(extraSortOptions ?? []).map((opt) => ({ value: opt.columnName, displayName: opt.displayName })),
    ];
    return options.length === 0 ? null : options;
  }, [config.filterableColumns, extraSortOptions]);
  const schema = useMemo(() => {
    const baseSchema = downloadRequestSchema().omit(['tableName', 'filters', 'columns']);
    if (sortByOptions === null) {
      return baseSchema.omit(['sortBy', 'sortOrder']);
    }
    return baseSchema;
  }, [sortByOptions]);

  const fieldsMap = useMemo(
    () =>
      schemaToFormFields(schema).reduce((map, field) => {
        if (field.key === 'sortBy') {
          return map.set(field.key, {
            ...field,
            rendererMeta: { ...(field.rendererMeta ?? {}), values: sortByOptions ?? [] },
          });
        }
        return map.set(field.key, field);
      }, new Map<string, IFormField<unknown>>()),
    [schema, sortByOptions]
  );

  const defaultValues = useMemo(() => {
    const defaults = schema.getDefault();
    return defaultSort ? ({ ...defaults, ...defaultSort } as unknown as typeof defaults) : defaults;
  }, [schema, defaultSort]);

  return { schema, fieldsMap, defaultValues };
}
